import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Send, AlertTriangle, ShieldCheck,
  Plus, Trash2, MessageSquare, ChevronRight, ToggleLeft, ToggleRight,
  Pill, TestTube2, Calendar, Syringe, FileText, Search, Stethoscope,
  ShoppingBag, Phone, Heart, Shield, Archive, Award, Clock, Droplets,
  Store, FlaskConical, Bot, User, ExternalLink, ChevronDown,
} from 'lucide-react';
import { patientApi } from '../api';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: { label: string; to: string; icon?: string }[];
  demoInsight?: boolean;
}

interface Conversation {
  id: string;
  title: string;
  lastMessage: string;
  updatedAt: Date;
  messages: ChatMessage[];
}

const QUICK_CHIPS = [
  { label: 'My health summary', icon: Heart, mode: 'health_summary' },
  { label: 'Explain my lab report', icon: TestTube2, mode: 'explain_lab' },
  { label: 'Explain prescription', icon: Pill, mode: 'explain_prescription' },
  { label: 'Medicine reminders', icon: Pill, mode: 'medicine_reminder' },
  { label: 'Vaccination status', icon: Syringe, mode: 'vaccination_reminder' },
  { label: 'Prepare doctor visit', icon: Stethoscope, mode: 'prepare_visit' },
  { label: 'Find a doctor', icon: Search, mode: 'find_doctor' },
  { label: 'Find a lab', icon: FlaskConical, mode: 'find_lab' },
  { label: 'Find pharmacy', icon: ShoppingBag, mode: 'find_pharmacy' },
  { label: 'Find hospital', icon: Store, mode: 'find_hospital' },
  { label: 'Book ambulance', icon: Phone, mode: 'find_ambulance' },
  { label: 'Insurance help', icon: Shield, mode: 'insurance_help' },
  { label: 'Emergency red-flag check', icon: AlertTriangle, mode: 'red_flag' },
];

function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

function formatTime(d: Date) {
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

async function generateResponse(mode: string, userText: string, permitted: boolean): Promise<ChatMessage> {
  const ts = new Date();
  // demoInsight drives the "From your NeoHealthCard records" badge — it must
  // default to FALSE and only be set on responses actually built from the
  // patient's records, never on errors/empty states/navigation replies.
  const base = { id: uid(), role: 'assistant' as const, timestamp: ts, demoInsight: false };

  if (!permitted && ['health_summary', 'explain_lab', 'explain_prescription', 'medicine_reminder', 'vaccination_reminder', 'prepare_visit', 'red_flag', 'insurance_help'].includes(mode)) {
    return { ...base, content: 'I need access to your NeoHealthCard data to help with this. Please enable the "Use my NeoHealthCard history" toggle above to continue.' };
  }

  try {
    if (mode === 'health_summary') {
      const [profile, dashboard] = await Promise.all([
        patientApi.getProfile().catch(() => null),
        patientApi.getDashboard().catch(() => null),
      ]);
      if (!profile) return { ...base, content: 'I couldn\'t load your profile data right now. Please try again later.' };
      const s = dashboard?.stats;
      return {
        ...base,
        demoInsight: true,
        content: `Here's your health overview, **${profile.fullName}**:\n\n` +
          `- **NHC ID**: ${profile.nhcId}\n` +
          `- **Blood Group**: ${profile.bloodGroup || 'Not recorded'}\n` +
          `- **Allergies**: ${profile.allergies?.length ? profile.allergies.join(', ') : 'None recorded'}\n` +
          `- **Chronic Conditions**: ${profile.chronicConditions?.length ? profile.chronicConditions.join(', ') : 'None recorded'}\n` +
          `- **Current Medications**: ${profile.medicines?.trim() || 'None recorded'}\n` +
          (s ? `\n**Activity**: ${s.upcomingAppointmentCount || 0} upcoming appointments, ${s.activeMedicineCount || 0} active medicines, ${s.pendingLabCount || 0} pending labs, ${s.activePolicyCount || 0} active insurance policies.` : '') +
          '\n\n*This is a summary of your recorded data — not a medical assessment.*',
        actions: [
          { label: 'View Timeline', to: '/patient/timeline' },
          { label: 'Health Vault', to: '/patient/health-vault' },
          { label: 'Update Profile', to: '/patient/settings' },
        ],
      };
    }

    if (mode === 'explain_lab') {
      const reports = await patientApi.getLabReports().catch(() => []);
      if (!reports?.length) return { ...base, content: 'No lab reports found in your records. Once your provider uploads results, I can help explain them.\n\n*I summarize data only — not a clinical interpretation.*' };
      // The app receives report-level data only (name/lab/date/summary/flags) —
      // never per-parameter values, so no claim about "parameters" is made.
      const r = reports[0] as any;
      const flags: string[] = r.abnormalFlags ?? [];
      return {
        ...base,
        demoInsight: true,
        content: `**Latest Lab Report**: ${r.testName || 'Lab Report'}\n` +
          `- **Lab**: ${r.labName || 'Unknown'}\n` +
          `- **Date**: ${r.reportDate ? new Date(r.reportDate).toLocaleDateString('en-IN') : 'N/A'}\n` +
          (r.summary ? `- **Lab's interpretation**: ${r.summary}\n` : '') +
          (flags.length ? `- **Flagged by the lab**: ${flags.join(', ')}\n` : '') +
          (r.isCritical ? `- ⚠ **Marked CRITICAL by the lab — contact your doctor now.**\n` : '') +
          '\n*I can only see the summary the lab shared — not individual test values, so I cannot judge whether values are normal. Open the full report and consult your doctor for interpretation.*',
        actions: [
          { label: 'View Lab Reports', to: '/patient/lab' },
          { label: 'Book Follow-up', to: '/patient/care-services/doctors' },
        ],
      };
    }

    if (mode === 'explain_prescription') {
      const medicines = await patientApi.getMedicines().catch(() => []);
      if (!medicines?.length) return { ...base, content: 'No active prescriptions found. Your medicines will appear here once prescribed by your doctor.\n\n*Always follow your doctor\'s prescription.*' };
      return {
        ...base,
        demoInsight: true,
        content: `You have **${medicines.length} active medicine(s)**:\n\n` +
          medicines.slice(0, 5).map((m: any) => `- **${m.medicineName}** ${m.dosage || ''} — ${m.frequency || ''} ${m.instructions ? '(' + m.instructions + ')' : ''}`).join('\n') +
          (medicines.length > 5 ? `\n- ...and ${medicines.length - 5} more` : '') +
          '\n\n*This lists your recorded medicines. For dosage questions or side effects, consult your prescribing doctor.*',
        actions: [
          { label: 'View Medicines', to: '/patient/medicines' },
          { label: 'Order Refills', to: '/patient/pharmacy' },
        ],
      };
    }

    if (mode === 'medicine_reminder') {
      const [medicines, todayMeds] = await Promise.all([
        patientApi.getMedicines().catch(() => []),
        patientApi.getTodayMedicines().catch(() => []),
      ]);
      if (!medicines?.length) return { ...base, content: 'No medicine reminders set up. Add your medicines to get timely reminders.\n\nYou can also auto-create reminders from any prescription — go to Medicine Reminders and use "Create from Prescription".\n\n*Always follow your doctor\'s prescription.*', actions: [{ label: 'Manage Medicines', to: '/patient/medicines' }] };
      const active = medicines.filter((m: any) => m.status === 'ACTIVE');
      const prescribed = active.filter((m: any) => m.source === 'PRESCRIPTION');
      const todayDoses = todayMeds.reduce((s: number, m: any) => {
        const times = typeof m.times === 'string' ? JSON.parse(m.times) : (m.times || []);
        return s + times.length;
      }, 0);
      const takenToday = todayMeds.reduce((s: number, m: any) => s + (m.adherenceLogs?.filter((l: any) => l.taken)?.length || 0), 0);
      return {
        ...base,
        demoInsight: true,
        content: `**Today's Medicine Schedule** — ${todayMeds.length} medicines, ${todayDoses} doses:\n\n` +
          todayMeds.slice(0, 8).map((m: any) => {
            const times = typeof m.times === 'string' ? JSON.parse(m.times) : (m.times || []);
            const taken = m.adherenceLogs?.filter((l: any) => l.taken)?.length || 0;
            return `- **${m.medicineName}** ${m.dosage || ''} — ${times.join(', ')} ${taken ? `(${taken}/${times.length} taken)` : '(pending)'}${m.source === 'PRESCRIPTION' ? ' 📋' : ''}`;
          }).join('\n') +
          `\n\n**Stats**: ${active.length} active medicines, ${prescribed.length} from prescriptions, ${takenToday}/${todayDoses} taken today.` +
          '\n\n*You can mark medicines as taken from the chat (Medicine Reminder Bot) or the Medicines page.*',
        actions: [
          { label: 'Manage Medicines', to: '/patient/medicines' },
          { label: 'Open Chat', to: '/patient/my-chat' },
        ],
      };
    }

    if (mode === 'vaccination_reminder') {
      const all = await patientApi.getCertificates({ type: 'VACCINATION_CERTIFICATE' }).catch(() => []);
      return {
        ...base,
        demoInsight: all.length > 0,
        content: all.length
          ? `**Vaccination Records** — ${all.length} certificates:\n\n` +
            all.slice(0, 6).map((c: any) => `- **${c.title}** — ${c.issuerName || 'Unknown provider'} (${c.issueDate ? new Date(c.issueDate).toLocaleDateString('en-IN') : 'N/A'})`).join('\n') +
            '\n\n*Vaccination schedules should be confirmed with your healthcare provider.*'
          : 'No vaccination certificates found. You can record your vaccinations on the Vaccinations page (self-reported entries), or ask your doctor to issue a certificate through NeoHealthCard.',
        actions: [
          { label: 'View Vaccinations', to: '/patient/vaccinations' },
          { label: 'Certificates', to: '/patient/certificates' },
          { label: 'Health IDs (ABHA)', to: '/patient/settings' },
        ],
      };
    }

    if (mode === 'prepare_visit') {
      const appointments = await patientApi.getAppointments().catch(() => []);
      const now = Date.now();
      const upcoming = (appointments || [])
        .filter((a: any) => (a.status === 'SCHEDULED' || a.status === 'CONFIRMED') && new Date(a.appointedAt).getTime() >= now)
        .sort((a: any, b: any) => new Date(a.appointedAt).getTime() - new Date(b.appointedAt).getTime());
      if (!upcoming.length) return { ...base, content: 'No upcoming appointments found. Would you like to book a doctor visit?', actions: [{ label: 'Find Doctor', to: '/patient/care-services/doctors' }] };
      const next = upcoming[0] as any;
      return {
        ...base,
        demoInsight: true,
        content: `**Prepare for your visit** with **${next.doctorName || 'your doctor'}**:\n\n` +
          `- **Date**: ${next.appointedAt ? new Date(next.appointedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'TBD'}\n` +
          `- **Reason**: ${next.reasonForVisit || 'Consultation'}\n\n` +
          `**Checklist**:\n` +
          `1. Bring your NeoHealthCard (NHC ID on phone)\n` +
          `2. Share recent lab reports with your doctor\n` +
          `3. List current symptoms and changes since last visit\n` +
          `4. Note questions you want to ask\n` +
          `5. Carry your insurance card if applicable\n\n` +
          '*You can share certificates and lab reports directly from your Health Vault.*',
        actions: [
          { label: 'View Follow-ups', to: '/patient/follow-ups' },
          { label: 'Health Vault', to: '/patient/health-vault' },
          { label: 'Share Documents', to: '/patient/share-center' },
        ],
      };
    }

    // Navigation replies — describe only what those pages actually do.
    if (mode === 'find_doctor') {
      return { ...base, content: 'I can help you find a doctor. Browse the registered doctors on NeoHealthCard and book an appointment.', actions: [{ label: 'Browse Doctors', to: '/patient/care-services/doctors' }] };
    }
    if (mode === 'find_lab') {
      return { ...base, content: 'Browse the labs registered on NeoHealthCard and book a test — at the lab or via home collection where the lab offers it.', actions: [{ label: 'Browse Labs', to: '/patient/care-services/labs' }] };
    }
    if (mode === 'find_pharmacy') {
      return { ...base, content: 'Order medicines from pharmacies that have opened their online store on NeoHealthCard — real catalogues and prices, paid at pickup or on delivery.', actions: [{ label: 'Browse Pharmacies', to: '/patient/care-services/pharmacy' }, { label: 'My Medicines', to: '/patient/medicines' }] };
    }
    if (mode === 'find_hospital') {
      return { ...base, content: 'Browse the hospitals registered on NeoHealthCard and book an OPD appointment.', actions: [{ label: 'Browse Hospitals', to: '/patient/care-services/hospitals' }] };
    }
    if (mode === 'find_ambulance') {
      return { ...base, content: '**For emergencies, use the SOS button** (bottom-right). For non-emergency medical transport, book an ambulance from Care Services.', actions: [{ label: 'Emergency SOS', to: '/patient/emergency' }, { label: 'Book Ambulance', to: '/patient/care-services/ambulance' }], demoInsight: false };
    }

    if (mode === 'insurance_help') {
      const [policies, claims] = await Promise.all([
        patientApi.getInsurancePolicies().catch(() => []),
        patientApi.getInsuranceClaims().catch(() => []),
      ]);
      if (!policies?.length) return { ...base, content: 'No insurance policies found in your records. Add your policy details in the Insurance section.', actions: [{ label: 'Insurance', to: '/patient/insurance' }] };
      const active = policies.filter((p: any) => p.status === 'ACTIVE');
      const pendingClaims = (claims || []).filter((c: any) => c.status === 'SUBMITTED' || c.status === 'IN_REVIEW' || c.status === 'PENDING');
      return {
        ...base,
        demoInsight: true,
        content: `**Insurance Overview**:\n\n` +
          `- **Active Policies**: ${active.length}\n` +
          active.slice(0, 3).map((p: any) => `  - ${p.providerName || 'Policy'}: ${p.planName || p.policyNumber || ''}`).join('\n') +
          `\n- **Pending Claims**: ${pendingClaims.length}\n` +
          (pendingClaims.length ? pendingClaims.slice(0, 3).map((c: any) => `  - ${c.claimNumber || c.id}: ₹${c.amount?.toLocaleString('en-IN') || '—'} (${c.status})`).join('\n') : '  None') +
          '\n\n*For claim disputes or policy questions, contact your insurer directly.*',
        actions: [
          { label: 'Insurance Details', to: '/patient/insurance' },
          { label: 'Claims Pack', to: '/patient/claims' },
        ],
      };
    }

    if (mode === 'red_flag') {
      const [profile, medicines, labs] = await Promise.all([
        patientApi.getProfile().catch(() => null),
        patientApi.getMedicines().catch(() => []),
        patientApi.getLabReports().catch(() => []),
      ]);
      const warnings: string[] = [];
      if (profile?.allergies?.length) warnings.push(`You have ${profile.allergies.length} recorded allergy(ies): ${profile.allergies.join(', ')}`);
      if (profile?.chronicConditions?.length) warnings.push(`Chronic conditions noted: ${profile.chronicConditions.join(', ')}`);
      const missedMeds = (medicines || []).filter((m: any) =>
        m.status === 'ACTIVE' && m.adherenceLogs?.some((l: any) => l.taken === false));
      if (missedMeds.length) warnings.push(`${missedMeds.length} medicine(s) may have missed doses recently`);
      // Only report-level flags exist in the app's data (abnormalFlags /
      // isCritical) — per-parameter values are never available here.
      const flaggedReports = (labs || []).filter((r: any) => (r.abnormalFlags?.length ?? 0) > 0 || r.isCritical);
      if (flaggedReports.length) {
        warnings.push(`${flaggedReports.length} lab report(s) carry abnormal/critical flags from the lab${flaggedReports.some((r: any) => r.isCritical) ? ' — at least one marked CRITICAL' : ''}`);
      }
      return {
        ...base,
        demoInsight: true,
        content: warnings.length
          ? `**Health Red-Flag Screening**:\n\n${warnings.map(w => `⚠ ${w}`).join('\n')}\n\n**This is NOT a medical diagnosis.** These are data-based observations from your records. If you feel unwell or notice symptoms, please consult your doctor immediately or use Emergency SOS.`
          : '**No flagged items in the records available to this app** (allergies, chronic conditions, missed doses, lab flags).\n\n**This does NOT mean everything is normal** — I cannot see individual test values or anything not recorded in NeoHealthCard. This is a data check, not a clinical assessment; if you feel unwell, consult your doctor or use Emergency SOS.',
        actions: [
          { label: 'Emergency SOS', to: '/patient/emergency' },
          { label: 'Find Doctor', to: '/patient/care-services/doctors' },
          { label: 'View Timeline', to: '/patient/timeline' },
        ],
      };
    }

    // Quick mode shortcuts from chips are already handled above (mode !== 'generic').
    // If it's a generic chat message, let the Gemini AI handle it — with the
    // patient's record context only when the consent toggle is on.
    try {
      const response = await patientApi.askNeoAI(userText, permitted);
      return {
        ...base,
        demoInsight: false,
        content: response.content || 'No response generated.',
      };
    } catch (e) {
      return {
        ...base, demoInsight: false,
        content: 'I can help you with your health data, services, and safety. (AI backend is currently unreachable)',
      };
    }
  } catch {
    return { ...base, demoInsight: false, content: 'Something went wrong while fetching your data. Please try again.' };
  }
}

export default function NeoAI() {
  const navigate = useNavigate();
  const [permitted, setPermitted] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>(() => [{
    id: uid(), title: 'New conversation', lastMessage: '', updatedAt: new Date(), messages: [],
  }]);
  const [activeConvId, setActiveConvId] = useState(conversations[0].id);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChips, setShowChips] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const activeConv = conversations.find(c => c.id === activeConvId) || conversations[0];
  const messages = activeConv.messages;

  const scrollToBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages.length, scrollToBottom]);

  const sendMessage = useCallback(async (text: string, mode?: string) => {
    if (!text.trim() && !mode) return;
    const userMsg: ChatMessage = { id: uid(), role: 'user', content: text.trim() || mode || '', timestamp: new Date() };
    setConversations(prev => prev.map(c => c.id === activeConvId ? {
      ...c, messages: [...c.messages, userMsg], lastMessage: userMsg.content, updatedAt: new Date(),
      title: c.messages.length === 0 ? userMsg.content.slice(0, 40) : c.title,
    } : c));
    setInput('');
    setShowChips(false);
    setThinking(true);
    scrollToBottom();

    const reply = await generateResponse(mode || 'generic', text.trim() || mode || '', permitted);
    setConversations(prev => prev.map(c => c.id === activeConvId ? {
      ...c, messages: [...c.messages, reply], lastMessage: reply.content.slice(0, 60), updatedAt: new Date(),
    } : c));
    setThinking(false);
    scrollToBottom();
  }, [activeConvId, permitted, scrollToBottom]);

  const newConversation = () => {
    const c: Conversation = { id: uid(), title: 'New conversation', lastMessage: '', updatedAt: new Date(), messages: [] };
    setConversations(prev => [c, ...prev]);
    setActiveConvId(c.id);
    setShowChips(true);
    setSidebarOpen(false);
  };

  const deleteConversation = (id: string) => {
    setConversations(prev => {
      const next = prev.filter(c => c.id !== id);
      if (!next.length) {
        const fresh: Conversation = { id: uid(), title: 'New conversation', lastMessage: '', updatedAt: new Date(), messages: [] };
        next.push(fresh);
      }
      if (activeConvId === id) setActiveConvId(next[0].id);
      return next;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  // Escape HTML first so message content (which can echo user input or contain
  // untrusted characters) can never inject markup — only our own tags below are
  // added afterwards. Prevents DOM XSS through dangerouslySetInnerHTML.
  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const renderMarkdown = (text: string) => {
    return text.split('\n').map((line, i) => {
      let html = escapeHtml(line)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 rounded text-[11px]">$1</code>');
      if (line.startsWith('- ')) html = `<span class="ml-2">• ${html.slice(2)}</span>`;
      if (line.startsWith('  - ')) html = `<span class="ml-6">◦ ${html.slice(4)}</span>`;
      if (/^\d+\. /.test(line)) html = `<span class="ml-2">${html}</span>`;
      return <span key={i} className="block leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
    });
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] -m-4 lg:-m-6">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Left Sidebar — conversation history */}
      <div className={`
        w-72 bg-white border-r border-gray-200 flex flex-col shrink-0
        fixed lg:relative z-50 h-full
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        transition-transform duration-200
      `}>
        <div className="p-3 border-b border-gray-100">
          <button onClick={newConversation}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> New Chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {conversations.map(c => (
            <div key={c.id}
              className={`group flex items-center gap-2 px-3 py-2.5 mx-1.5 rounded-lg cursor-pointer transition-colors ${
                c.id === activeConvId ? 'bg-violet-50 text-violet-800' : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => { setActiveConvId(c.id); setSidebarOpen(false); setShowChips(c.messages.length === 0); }}
            >
              <MessageSquare className="w-4 h-4 shrink-0 opacity-50" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">{c.title}</p>
                {c.lastMessage && <p className="text-[10px] text-gray-400 truncate mt-0.5">{c.lastMessage}</p>}
              </div>
              {conversations.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); deleteConversation(c.id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-gray-100 text-[9px] text-gray-400 text-center">
          NeoAI is assistive only — not diagnostic
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1 text-gray-500">
              <MessageSquare className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">NeoAI</p>
                <p className="text-[10px] text-gray-400">Health Assistant • Assistive only, not diagnostic</p>
              </div>
            </div>
          </div>

          {/* Consent toggle */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500 hidden sm:block">Use my NHC data</span>
            <button onClick={() => setPermitted(!permitted)} className="shrink-0" title="Allow NeoAI to access your NeoHealthCard data">
              {permitted
                ? <ToggleRight className="w-8 h-8 text-emerald-600" />
                : <ToggleLeft className="w-8 h-8 text-gray-300" />
              }
            </button>
            {permitted && <ShieldCheck className="w-4 h-4 text-emerald-500" />}
          </div>
        </div>

        {/* Emergency escalation banner */}
        <div className="bg-red-50 border-b border-red-100 px-4 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px] text-red-700">
            <AlertTriangle className="w-3 h-3" />
            <span><strong>Feeling a medical emergency?</strong> NeoAI cannot provide emergency help.</span>
          </div>
          <button onClick={() => navigate('/patient/emergency')}
            className="text-[10px] font-bold text-red-700 bg-red-100 hover:bg-red-200 px-2 py-0.5 rounded transition-colors flex items-center gap-1">
            Emergency SOS <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-violet-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Hello! I'm NeoAI</h2>
              <p className="text-sm text-gray-500 max-w-md mb-1">Your NeoHealthCard health assistant. I can help you understand your health records, find providers, manage medicines, and prepare for visits.</p>
              <p className="text-[10px] text-amber-600 font-medium mb-6">Assistive only — I do not provide medical diagnoses or treatment advice.</p>

              {showChips && (
                <div className="flex flex-wrap justify-center gap-2 max-w-2xl">
                  {QUICK_CHIPS.map(chip => {
                    const Icon = chip.icon;
                    return (
                      <button key={chip.mode} onClick={() => sendMessage(chip.label, chip.mode)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs text-gray-700 hover:bg-violet-50 hover:border-violet-200 hover:text-violet-700 transition-colors shadow-sm">
                        <Icon className="w-3.5 h-3.5" />
                        {chip.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {messages.map(msg => (
            <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="w-4 h-4 text-white" />
                </div>
              )}
              <div className={`max-w-[75%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-violet-600 text-white rounded-tr-md'
                    : 'bg-white border border-gray-200 text-gray-800 rounded-tl-md shadow-sm'
                }`}>
                  {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
                </div>
                {msg.demoInsight && msg.role === 'assistant' && (
                  <span className="inline-block text-[9px] text-violet-500 bg-violet-50 px-1.5 py-0.5 rounded mt-1 font-medium">From your NeoHealthCard records</span>
                )}
                {msg.actions && msg.actions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {msg.actions.map(a => (
                      <button key={a.label} onClick={() => navigate(a.to)}
                        className="flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-[11px] font-medium text-gray-700 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200 transition-colors shadow-sm">
                        <ExternalLink className="w-3 h-3" />
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
                <p className={`text-[9px] mt-1 ${msg.role === 'user' ? 'text-right text-gray-400' : 'text-gray-400'}`}>
                  {formatTime(msg.timestamp)}
                </p>
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-lg bg-teal-600 flex items-center justify-center shrink-0 mt-0.5">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}

          {thinking && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center shrink-0">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          {/* Safety guideline — always visible in chat area */}
          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl mx-auto max-w-xl mt-2">
            <ShieldCheck className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-700 leading-relaxed">
              <strong>NeoAI is assistive only</strong> — not a medical diagnosis system. Always consult your healthcare provider for medical decisions. All interactions are subject to patient consent.
            </p>
          </div>

          <div ref={messagesEndRef} />
        </div>

        {/* Quick chips when there are messages */}
        {messages.length > 0 && (
          <div className="px-4 pb-1">
            <button onClick={() => setShowChips(v => !v)} className="text-[10px] text-violet-500 hover:text-violet-700 flex items-center gap-0.5 mb-1">
              <ChevronDown className={`w-3 h-3 transition-transform ${showChips ? 'rotate-180' : ''}`} />
              {showChips ? 'Hide suggestions' : 'Show suggestions'}
            </button>
            {showChips && (
              <div className="flex flex-wrap gap-1.5 pb-2">
                {QUICK_CHIPS.slice(0, 8).map(chip => {
                  const Icon = chip.icon;
                  return (
                    <button key={chip.mode} onClick={() => sendMessage(chip.label, chip.mode)}
                      className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-full text-[10px] text-gray-600 hover:bg-violet-50 hover:border-violet-200 hover:text-violet-700 transition-colors">
                      <Icon className="w-3 h-3" /> {chip.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Composer */}
        <div className="bg-white border-t border-gray-200 px-4 py-3">
          <div className="flex items-end gap-2 max-w-3xl mx-auto">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask NeoAI about your health records..."
                rows={1}
                className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 pr-10 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
                style={{ maxHeight: '120px' }}
                onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = Math.min(t.scrollHeight, 120) + 'px'; }}
              />
            </div>
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || thinking}
              className="p-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-xl transition-colors shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
